import { defineStore } from "pinia";
import { ref, computed } from "vue";
import type { User } from "@/types";
import {
  api,
  signInWithEmail,
  signUpWithEmail,
  signOut,
  getCurrentUser,
} from "@/lib/api";

type AuthError = { message: string };

export const useAuthStore = defineStore("auth", () => {
  // State
  const user = ref<User | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const isAuthenticated = computed(() => user.value !== null);
  const currentUser = computed(() => user.value);

  // Actions
  const setUser = (userData: User | null) => {
    user.value = userData;
  };

  const setLoading = (loading: boolean) => {
    isLoading.value = loading;
  };

  const setError = (errorMessage: string | null) => {
    error.value = errorMessage;
  };

  const login = async (email: string, password: string) => {
    setLoading(true);
    setError(null);

    try {
      const { user: authUser } = await signInWithEmail(email, password);

      if (authUser) {
        const userData: User = {
          id: authUser.id,
          email: authUser.email!,
          firstName:
            authUser.first_name || authUser.user_metadata?.first_name || null,
          lastName:
            authUser.last_name || authUser.user_metadata?.last_name || null,
          avatar:
            authUser.avatar_url || authUser.user_metadata?.avatar_url || null,
          createdAt: authUser.created_at,
          updatedAt: authUser.updated_at || authUser.created_at,
        };

        setUser(userData);

        // Clear router auth cache so guard picks up the new token immediately
        const { clearAuthCache } = await import("@/router");
        clearAuthCache();

        console.log("Store login successful:", { email });
      }
    } catch (err) {
      const authError = err as AuthError;
      const message = authError.message || "Login failed";
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const register = async (
    email: string,
    password: string,
    firstName?: string,
    lastName?: string,
  ) => {
    setLoading(true);
    setError(null);

    try {
      const metadata = {
        first_name: firstName,
        last_name: lastName,
      };

      const { user: authUser } = await signUpWithEmail(
        email,
        password,
        metadata,
      );

      if (authUser) {
        const userData: User = {
          id: authUser.id,
          email: authUser.email!,
          firstName:
            authUser.first_name ||
            authUser.user_metadata?.first_name ||
            firstName ||
            null,
          lastName:
            authUser.last_name ||
            authUser.user_metadata?.last_name ||
            lastName ||
            null,
          avatar:
            authUser.avatar_url || authUser.user_metadata?.avatar_url || null,
          createdAt: authUser.created_at,
          updatedAt: authUser.updated_at || authUser.created_at,
        };

        setUser(userData);

        // Clear router auth cache so guard picks up the new token immediately
        const { clearAuthCache } = await import("@/router");
        clearAuthCache();

        console.log("Store register successful:", {
          email,
          firstName,
          lastName,
        });
      }
    } catch (err) {
      const authError = err as AuthError;
      const message = authError.message || "Registration failed";
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    setError(null);

    try {
      await signOut();
      setUser(null);

      // Clear router authentication cache
      const { clearAuthCache } = await import("@/router");
      clearAuthCache();

      console.log("User logged out from store");
    } catch (err) {
      const authError = err as AuthError;
      const message = authError.message || "Logout failed";
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const initializeAuth = async () => {
    setLoading(true);
    setError(null);

    try {
      const authUser = await getCurrentUser();

      if (authUser) {
        const userData: User = {
          id: authUser.id,
          email: authUser.email!,
          firstName: authUser.user_metadata?.first_name || null,
          lastName: authUser.user_metadata?.last_name || null,
          avatar: authUser.user_metadata?.avatar_url || null,
          createdAt: authUser.created_at,
          updatedAt: authUser.updated_at || authUser.created_at,
        };

        setUser(userData);
        console.log("Auth state restored:", userData.email);
      } else {
        console.log("No existing session found");
      }

      // JWT-based auth: token in localStorage, no realtime listener needed
    } catch (err) {
      const authError = err as AuthError;
      const message = authError.message || "Auth initialization failed";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    setLoading(true);
    setError(null);

    try {
      const res = await api.post("/auth/forgot-password", { email });
      if (res.error) throw { message: res.error.message };

      console.log("Password reset email sent to:", email);
      return true;
    } catch (err) {
      const authError = err as AuthError;
      const message = authError.message || "Failed to send reset email";
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async (newPassword: string) => {
    setLoading(true);
    setError(null);

    try {
      const res = await api.put("/auth/user", { password: newPassword });
      if (res.error) throw { message: res.error.message };

      console.log("Password updated successfully");
      return true;
    } catch (err) {
      const authError = err as AuthError;
      const message = authError.message || "Failed to update password";
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (profileData: {
    firstName?: string;
    lastName?: string;
    email?: string;
    avatar?: string;
  }) => {
    setLoading(true);
    setError(null);

    try {
      const updates: any = {};

      // Handle email update separately if provided
      if (profileData.email && profileData.email !== user.value?.email) {
        updates.email = profileData.email;
      }

      // Handle metadata updates
      const metadataUpdates: any = {};
      if (profileData.firstName !== undefined) {
        metadataUpdates.first_name = profileData.firstName;
      }
      if (profileData.lastName !== undefined) {
        metadataUpdates.last_name = profileData.lastName;
      }
      if (profileData.avatar !== undefined) {
        metadataUpdates.avatar_url = profileData.avatar;
      }

      if (Object.keys(metadataUpdates).length > 0) {
        updates.data = metadataUpdates;
      }

      const res = await api.put("/auth/user", updates);
      if (res.error) throw { message: res.error.message };

      // Update local user state
      if (user.value) {
        const updatedUser: User = {
          ...user.value,
          firstName:
            profileData.firstName !== undefined
              ? profileData.firstName
              : user.value.firstName,
          lastName:
            profileData.lastName !== undefined
              ? profileData.lastName
              : user.value.lastName,
          email:
            profileData.email !== undefined
              ? profileData.email
              : user.value.email,
          avatar:
            profileData.avatar !== undefined
              ? profileData.avatar
              : user.value.avatar,
          updatedAt: new Date().toISOString(),
        };

        setUser(updatedUser);
      }

      console.log("Profile updated successfully");
      return true;
    } catch (err) {
      const authError = err as AuthError;
      const message = authError.message || "Failed to update profile";
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  return {
    // State
    user,
    isLoading,
    error,

    // Getters
    isAuthenticated,
    currentUser,

    // Actions
    setUser,
    setLoading,
    setError,
    login,
    register,
    logout,
    initializeAuth,
    resetPassword,
    updatePassword,
    updateProfile,
    clearError,
  };
});
